import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "../pages/CustomButton";
import { sub } from "date-fns";

const buildHref = (path, sub) => {
  const id = sub.substate_id ?? sub.id ?? sub.state_id ?? sub.state?.id ?? "";
  return `${path}?state_id=${encodeURIComponent(id)}`;
};

const normalizeSummaryRow = (row) => ({
  id: row.substate_id ?? row.id ?? null,
  name: row.substate_name ?? row.name ?? "-",
  state_id: row.state_id ?? row.headquarter_id ?? null,
  state_name: row.state_name ?? row.headquarter_name ?? row.state?.name ?? "-",
  number_of_city: Number(row.city_count ?? row.cities_count ?? 0),
  doctors_count: Number(row.doctor_count ?? row.total_doctors ?? row.doctors_count ?? 0),
  chemist_count: Number(row.chemist_count ?? row.total_chemists ?? row.chemists_count ?? 0),
  stockist_count: Number(row.stockist_count ?? row.total_stockists ?? row.stockists_count ?? 0),
  raw: row,
});

const SubstateManager = () => {
  const [substates, setSubstates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [headquarters, setHeadquarters] = useState([]);

  // Inline edit
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editStateId, setEditStateId] = useState(null);

  // Add modal
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newStateId, setNewStateId] = useState("");
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/states`, {
        headers: getAuthHeaders(),
      })
      .then((res) => setStates(res.data))
      .catch((err) => {
        console.error("Error fetching states:", err);
      });
  }, []);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchHeadquarters(), fetchSubstates()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchHeadquarters = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/states`, {
        headers: getAuthHeaders(),
      });
      setHeadquarters(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching headquarters:", err);
      alert("Failed to fetch headquarters");
    }
  };

  const fetchSubstates = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/substate-summary`, {
        headers: getAuthHeaders(),
      });

      const raw = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];

      const normalized = raw.map(normalizeSummaryRow);
      setSubstates(normalized);
    } catch (err) {
      console.error("Error fetching substates:", err);
      alert("Failed to fetch substates");
    }
  };

  const fetchCityData = async () => {
    if (!selectedState) {
      alert("Please select a state");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/api/substate-summary?state_id=${selectedState}`,
        {
          headers: getAuthHeaders(),
        }
      );

      const data = Array.isArray(res.data)
        ? res.data
        : res.data?.data || [];

      const normalized = data.map(normalizeSummaryRow);
      setSubstates(normalized);
    } catch (err) {
      console.error("Error fetching substates:", err);
      alert("Failed to fetch substate data");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (sub) => {
    setEditing(sub);
    setEditName(sub.name || "");
    const sid = sub.state_id ?? sub.headquarter_id ?? (sub.state && sub.state.id) ?? null;
    setEditStateId(sid ?? "");
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditName("");
    setEditStateId(null);
  };

  const updateSubstate = async (id) => {
    if (!editName.trim() || !editing) {
      alert("Please enter substate name");
      return;
    }

    if (!editStateId) {
      alert("Please select parent state");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: editName.trim(),
        state_id: Number(editStateId),
        substate_id: Number(id),
      };

      const idToUse = editing.id ?? id;
      const res = await axios.put(
        `${BASE_URL}/api/substates/${Number(idToUse)}`,
        payload,
        {
          headers: getAuthHeaders(),
        }
      );

      if (res.status === 200 || res.status === 204) {
        alert("Substate updated");
        cancelEdit();
        await fetchSubstates();
      } else {
        console.warn("Unexpected update response", res);
        alert("Failed to update substate");
      }
    } catch (err) {
      console.error("Update error:", err);
      alert("Failed to update substate");
    } finally {
      setSaving(false);
    }
  };

  const deleteSubstate = async (sub) => {
    if (!window.confirm("Are you sure you want to delete this substate?")) return;

    const resolvedStateId =
      sub.state_id ?? sub.headquarter_id ?? (sub.state && sub.state.id) ?? sub.id ?? null;

    setSaving(true);
    try {
      const payload = {
        name: sub.name || "",
        state_id: resolvedStateId ? Number(resolvedStateId) : undefined,
      };

      const idToUse = sub.id ?? sub.substate_id ?? sub.state_id;
      const res = await axios.delete(`${BASE_URL}/api/substates/${Number(idToUse)}`, {
        headers: getAuthHeaders(),
        data: payload,
      });

      if (res.status === 200 || res.status === 204) {
        alert("Substate deleted");
        await fetchSubstates();
      } else {
        console.warn("Unexpected delete response", res);
        alert("Failed to delete substate");
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete substate");
    } finally {
      setSaving(false);
    }
  };

  const addSubstate = async (e) => {
    e && e.preventDefault();
    if (!newName.trim() || !newStateId) {
      alert("Please fill required fields");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: newName.trim(),
        state_id: Number(newStateId),
      };
      const res = await axios.post(`${BASE_URL}/api/substates`, payload, {
        headers: getAuthHeaders(),
      });
      if (res.status === 201 || res.status === 200) {
        alert("Substate added");
        setNewName("");
        setNewStateId("");
        setIsAddOpen(false);
        await fetchSubstates();
      } else {
        console.warn("Unexpected add response", res);
        alert("Failed to add substate");
      }
    } catch (err) {
      console.error("Add error:", err);
      alert("Failed to add substate");
    } finally {
      setSaving(false);
    }
  };

  const stateNameFor = (sub) => {
    return (
      sub.state_name ||
      (sub.state && sub.state.name) ||
      sub.headquarter_name ||
      sub.headquarter?.name ||
      headquarters.find((h) => h.id === (sub.state_id ?? sub.headquarter_id ?? null))?.name ||
      "-"
    );
  };

  return (
    <div className="p-6 border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700"> Substate </span>
          <span className="text-blue-500">Management</span>
        </h1>

        <CustomButton
          onClick={() => {
            fetchHeadquarters().then(() => setIsAddOpen(true));
          }}
          variant="add"
          label="Add Substate"
        />
      </div>

      <div className="city-filter-section mb-4">
        <label className="city-filter-label">Select State:</label>

        <select
          className="city-filter-dropdown"
          value={selectedState}
          onChange={(e) => setSelectedState(e.target.value)}
        >
          <option value="">-- Select --</option>
          {states.map((state) => (
            <option key={state.id} value={state.id}>
              {state.name}
            </option>
          ))}
        </select>

        <CustomButton className="city-btn-go" onClick={fetchCityData}>
          Go
        </CustomButton>
      </div>

      <div className="border rounded shadow table-wrapper">
        <table className="unlock-dcr-leave-table text-sm">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Code</th>
              <th>State</th>
              <th>Substate</th>
              <th>No of City</th>
              <th>No of Doctors</th>
              <th>No of Chemist</th>
              <th>No of Stockist</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="p-4 text-center">
                  Loading...
                </td>
              </tr>
            ) : substates.length === 0 ? (
              <tr>
                <td colSpan="9" className="p-4 text-center">
                  No substates found
                </td>
              </tr>
            ) : (
              substates.map((sub, idx) => (
                <tr key={sub.id ?? idx} className="text-center">
                  <td className="p-2 border">{idx + 1}</td>
                  <td className="p-2 border">{sub.id ?? "-"}</td>

                  {/* State column - editable dropdown in edit mode */}
                  <td className="p-2 border">
                    {editing && editing.id === sub.id ? (
                      <select
                        value={editStateId || ""}
                        onChange={(e) => setEditStateId(e.target.value)}
                        className="border px-2 py-1 w-full"
                      >
                        <option value="">-- Select State --</option>
                        {headquarters.map((h) => (
                          <option key={h.id} value={h.id}>
                            {h.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      stateNameFor(sub)
                    )}
                  </td>

                  {/* Substate name column - editable in edit mode */}
                  <td className="p-2 border">
                    {editing && editing.id === sub.id ? (
                      <input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="border px-2 py-1 w-full"
                      />
                    ) : (
                      sub.name
                    )}
                  </td>

                  <td className="p-2 border">
                    <a
                      href={buildHref("/cities", sub)}
                      className="text-blue-600 underline"
                      title={`View Cities for ${sub.name}`}
                    >
                      {sub.number_of_city ?? 0}
                    </a>
                  </td>
                  <td className="p-2 border">
                    <a
                      href={buildHref("/doctors", sub)}
                      className="text-blue-600 underline"
                      title={`View Doctors for ${sub.name}`}
                    >
                      {sub.doctors_count ?? 0}
                    </a>
                  </td>
                  <td className="p-2 border">
                    <a
                      href={buildHref("/chemists", sub)}
                      className="text-blue-600 underline"
                      title={`View Chemists for ${sub.name}`}
                    >
                      {sub.chemist_count ?? 0}
                    </a>
                  </td>
                  <td className="p-2 border">
                    <a
                      href={buildHref("/stockists", sub)}
                      className="text-blue-600 underline"
                      title={`View Stockists for ${sub.name}`}
                    >
                      {sub.stockist_count ?? 0}
                    </a>
                  </td>

                  <td className="p-2 border">
                    {editing && editing.id === sub.id ? (
                      <>
                        <CustomButton
                          onClick={() => updateSubstate(sub.id)}
                          variant="Save"
                          className="bg-green-500 text-white px-3 py-1 rounded mr-2"
                          label={saving ? "Saving..." : "Save"}
                          disabled={saving}
                        />
                        <CustomButton
                          onClick={cancelEdit}
                          variant="Cancel"
                          className="bg-gray-500 text-white px-3 py-1 rounded"
                          label="Cancel"
                        />
                      </>
                    ) : (
                      <>
                        <CustomButton
                          onClick={() => {
                            fetchHeadquarters().then(() => startEdit(sub));
                          }}
                          variant="edit"
                          label="Edit"
                        />
                        <CustomButton
                          onClick={() => deleteSubstate(sub)}
                          variant="delete"
                          label={saving ? "Please wait" : "Delete"}
                          disabled={saving}
                        />
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div
            className="absolute inset-0 bg-black opacity-40"
            onClick={() => setIsAddOpen(false)}
          />
          <div className="relative bg-white rounded shadow-lg w-full max-w-lg z-10 p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Add Substate</h2>
              <CustomButton
                onClick={() => setIsAddOpen(false)}
                className="text-gray-600"
                type="button"
              >
                Close
              </CustomButton>
            </div>

            <form onSubmit={addSubstate} className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium">Select state</label>
                  <select
                    value={newStateId}
                    onChange={(e) => setNewStateId(e.target.value)}
                    required
                    className="mt-1 block w-full border px-2 py-1"
                  >
                    <option value="">-- Select state --</option>
                    {headquarters.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium">Substate Name</label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Enter substate"
                    required
                    className="mt-1 block w-full border px-2 py-1"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-4">
                <CustomButton
                  type="submit"
                  className="px-4 py-2 rounded bg-green-600 text-white"
                  disabled={saving}
                >
                  {saving ? "Submitting..." : "Submit"}
                </CustomButton>
                <CustomButton
                  variant="cancel"
                  onClick={() => {
                    setIsAddOpen(false);
                    setNewName("");
                    setNewStateId("");
                  }}
                  type="button"
                >
                  Cancel
                </CustomButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubstateManager;